// src/models/table-configuration.dto.ts

/**
 * Single table configuration
 */
export interface TableConfig {
	rowHeight: number; // Generic row height for the entire table
	columnWidths: Record<string, number>; // Per column: { "employeeName": 300, "email": 250 }
	columnOrder: string[]; // Array of column IDs in display order
	columnVisibility: Record<string, boolean>; // { "employeeName": true, "email": false }
}

/**
 * All table configurations for a user (sent to backend as single JSON)
 */
export interface UserTableConfigurations {
	userId: number;
	tables: Record<string, TableConfig>; // Key is tableId, value is config
	updatedAt?: string;
}

/**
 * Request/Response DTOs
 */
export interface SaveUserTableConfigurationsRequest {
	userId: number;
	tables: Record<string, TableConfig>;
}

export interface SaveUserTableConfigurationsResponse {
	success: boolean;
	data: UserTableConfigurations;
	message?: string;
}

/**
 * Table IDs - Use these constants across your app for consistency
 */
export enum TableId {
	// Employee Tables
	EMPLOYEES = "employees",
	EMPLOYEE_HISTORY = "employee-history",
	EMPLOYEE_HISTORY_ENGAGEMENT = "employee-history-engagement",
	EMPLOYEE_HISTORY_SALARY = "employee-history-salary",
	EMPLOYEE_HISTORY_JOB_TITLE = "employee-history-job-title",
	EMPLOYEE_HISTORY_BAND = "employee-history-band",
	EMPLOYEE_HISTORY_NOTCH = "employee-history-notch",
	EMPLOYEE_HISTORY_ALLOWANCE = "employee-history-allowance",
	EMPLOYEE_HISTORY_QUALIFICATION = "employee-history-qualification",
	EMPLOYEE_HISTORY_DISENGAGEMENT = "employee-history-disengagement",

	// Payroll Tables
	PAYROLL_RUNS = "payroll-runs",
	PAYSLIPS = "payslips",

	// Report Tables
	SALARY_REPORT = "salary-report",
	SSF_REPORT = "ssf-report",
	SAVING_SCHEME_REPORT = "saving-scheme-report",
	BANK_REPORT = "bank-report",
	TAX_REPORT = "tax-report",
	PAYROLL_SUMMARY = "payroll-summary",
	SALARY_ADVANCE_REPORT = "salary-advance-report",
	PAYE_SUMMARY_REPORT = "PAYE_SUMMARY_REPORT",
	EMPLOYEE_ANNUAL_TAX_DEDUCTION = "EMPLOYEE_ANNUAL_TAX_DEDUCTION",

	// Announcement Tables
	ANNOUNCEMENTS = "announcements",

	// custom tax reliefs table
	CUSTOM_TAX_RELIEFS = "custom-tax-relief",

	// Configuration Tables
	DOCUMENT_TYPES = "document-types",

	CURRENCY_EXCHANGE = "currency-exchange",

	// pay advance tables
	SALARY_ADVANCE = "salary-advance",
	PARTIAL_SALARY = "partial_salary",

	// salary adjustments
	SALARY_ADJUSTMENTS = "salary-adjustments",

	// benefit tables
	ALLOWANCE = "allowance",
	BENEFITS = "benefits",
	LOAN_BENEFITS = "loan-benefits",

	// savings and backpay tables
	SAVINGS = "savings-scheme",
	BACK_PAY = "back-pay",

	// overtime and bonuses
	BONUS = "bonus",
	OVERTIME = "overtime",

	// loans and explicit deductions
	LOANS = "loans",
	EXPLICIT_DEDUCTIONS = "explicit-deductions",

	// work time and overtime manangement
	WORK_TIME = "work-time",
	OVERTIME_MANAGEMENT = "overtime-management",
	WORK_TIME_REQUESTS = "work-time-requests",
	OVERTIME_REQUESTS = "overtime-requests",

	// grievances and disciplinary
	DISCIPLINARY = "disciplinary",
	GRIEVANCES = "grievances",

	// leave management
	LEAVE_REQUESTS = "leave-requests",
	LEAVE_PACKAGES = "leave-packages",

	// company settings
	COMPANY_CURRENCIES = "company-currencies",
	DEPARTMENTS = "departments",
	EMPLOYEE_BANDS = "employee-bands",
	ERP_MAPPING_INTEGRATION = "erp-mapping-integration",
	BONUS_GROUPS = "bonus-groups",
	DISCIPLINARY_TYPES = "disciplinary-types",
	GRIEVANCE_TYPES = "grievance-types",
	ALLOWANCE_GROUPS = "allowance-groups",
	BACK_PAY_GROUPS = "back-pay-groups",
	BENEFIT_GROUPS = "benefit-group",
	CUSTOM_TAX_RELIEF_GROUPS = "custom-tax-relief-groups",
	LOAN_BENEFIT_GROUPS = "loan-benefit-groups",
	OVERTIME_GROUPS = "overtime-groups",
	PARTIAL_SALARY_GROUPS = "partial-salary-groups",
	SALARY_ADVANCE_GROUPS = "salary-advance-groups",

	// self service
	LEAVE_REQUESTS_SELF = "leave-requests-self",
	LEAVE_PLANS_SELF = "leave-plans-self",
	SUPERVISEES_LEAVE_REQUESTS = "supervisees-leave-requests",
	SUPERVISEES_LEAVE_PLANS = "supervisees-leave-plans",
	WORK_TIME_SELF = "work-time-self",
	OVERTIME_SELF = "overtime-self",
	WORK_TIME_REQUESTS_SELF = "work-time-requests-self",
	OVERTIME_REQUESTS_SELF = "overtime-requests-self",
	WORK_TIME_REQUEST_APPROVALS = "work-time-request-approvals",
	OVERTIME_REQUEST_APPROVALS = "overtime-request-approvals",
	PAYSLIPS_SELF = "payslips-self",
	SELF_CLAIMS = "self-claims",
	SUPERVISEES_CLAIMS = "supervisees-claims",
	RECENT_PAY = "recent-pay",

	// admin - system configuration
	BENEFIT_TYPES = "benefit-types",
	COUNTRIES = "countries",
	CURRENCIES = "currencies",
	EXCHANGE_RATES = "exchange-rates",
	INDUSTRIES = "industries",
	PAYMENT_INSTITUTIONS = "payment-institutions",
	PAYMENT_INSTITUTIONS_BRANCH = "payment-institutions-branch",
	LEAVE_TYPES = "leave-types",
	NATIONALITIES = "nationalities",
	PENSIONS = "pensions",
	PROMOTIONS = "promotions",
	SUBSCRIPTION_PACKAGES = "subscription-packages",
	TAX_CODES = "tax-codes",
	TAX_RELIEF_TYPES = "tax-relief-types",
	BENEFITS_TAX_RULE = "benefits-tax-rule",
	RATE_TAX_RULE = "rate-tax-rule",
	RELIEF_TAX_RULE = "relief-tax-rule",
	COMPANY_IDS = "company-ids",
	ACCOUNT_MANAGEMENT = "account-management",
	INVOICES = "invoices",
	TRANSACTIONS = "transactions",

	// HR
	REIMBURSEMENTS = "reimbursements",

	// Account Settings
	HOLIDAYS = "holidays",
	TEAM_MEMBERS = "team-members",

	COMPANY_TAX_CODES = "company-tax-codes",
}
